import React, { useCallback, useEffect, useState } from 'react';
import styles from './EvaluationQuestions.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Table from '../../../components/table/Table';
import Search from '../../../components/search/Search';
import Filter from '../../../components/filter/Filter';
import Dropdown from '../../../components/dropdown/Dropdown';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';

const EvaluationQuestions = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [specialities, setSpecialities] = useState([{ value: null, label: 'All' }]);
  const [selectedSpeciality, setSelectedSpeciality] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false });

  useEffect(() => {
    const fetchYears = async () => {
      setIsLoading(true);
      try {
        const [yearsRes, specialitiesRes] = await Promise.all([
          axiosClient.get('years/details'),
          axiosClient.get('specialities'),
        ]);
        const yearOptions = yearsRes.data.map((item) => ({ label: item.year.year, value: item.year.id }));
        setYears(yearOptions);
        if (yearOptions.length) setSelectedYear(yearOptions[0].value);
        const specialityOptions = specialitiesRes.data.map((item) => ({ value: item.id, label: item.name }));
        setSpecialities([{ value: null, label: 'All' }, ...specialityOptions]);
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchYears();
  }, [showToast]);

  const fetchQuestions = useCallback(async (page = 1) => {
    if (!selectedYear) return;
    setIsLoading(true);
    try {
      const params = { page, limit: 10, search };
      if (selectedYear) params.yearId = selectedYear;
      if (selectedSpeciality) params.specialityId = selectedSpeciality;
      const response = await axiosClient.get('/evaluation-questions', { params });
      const responseData = response.data;
      setData(responseData.data || []);
      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages,
      });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [search, selectedYear, selectedSpeciality, showToast]);

  useEffect(() => {
    fetchQuestions(1);
  }, [fetchQuestions]);

  const handlePageChange = (page) => {
    fetchQuestions(page);
  };

  const columns = [
    {
      header: 'Question',
      render: (row) => (
        <span className={styles.questionLink} onClick={() => navigate(`/evaluation-questions/edit/${row.id}`)}>
          {row.question}
        </span>
      ),
    },
    {
      header: 'Type',
      render: (row) => row.type,
    },
{
      header: 'Specialities',
      render: (row) => row.specialities?.map((s) => s.name).join(', ') || '-',
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Evaluation Questions" />
        <Search onSubmit={setSearch} />
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={years}
              selected={selectedYear}
              onChange={(value) => setSelectedYear(value)}
            />
          </div>
          <div className={styles.specialityFilter}>
            <Dropdown
              options={specialities}
              value={selectedSpeciality}
              onChange={(value) => setSelectedSpeciality(value)}
            />
          </div>
        </div>
        <Table
          columns={columns}
          data={data}
          emptyMessage="No evaluation questions found."
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default EvaluationQuestions;
